// BHP-1083: the keyboard/focus contract a modal dialog owes its users
// (WCAG 2.2: 2.1.2 No Keyboard Trap, 2.4.3 Focus Order, 2.4.11 Focus Not
// Obscured). `role="dialog"` alone announces a dialog without behaving like
// one: focus stays behind it and Escape does nothing.
//
// On mount: focus moves into the dialog. While open: Tab cycles inside it and
// Escape closes. On unmount: focus returns to the element that opened it.
import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useDialogA11y<T extends HTMLElement>(
  onClose: () => void
): React.RefObject<T> {
  const dialogRef = useRef<T>(null);
  // Keep the latest handler without re-running the effect (and so re-stealing
  // focus) whenever the parent re-renders with a new closure.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusable = (): HTMLElement[] =>
      Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));

    focusable()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const elements = focusable();
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (first === undefined || last === undefined) {
        return;
      }
      // Wrap at both ends so focus never escapes to the page behind.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  return dialogRef;
}
